import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import Router from 'next/router'
import { v4 as uuid } from 'uuid'
import clsx from 'clsx'
import compose from 'recompose/compose'
import { connect } from 'react-redux'
import { withI18next } from 'lib/i18n/withI18next'
import { SEGMENT_STORAGE_KEY_PRODUCT } from 'lib/segment/const'
import { appendObjToSessionObj } from 'lib/utils/common/commonUtils'
import Typo from 'constants/typography'
import ColorIcon from './color-icon'
import BoxIcon from './box-icon'

function onClickChangeProduct(as, params) {
  const { productId, idx, variations } = params
  const pageUuid = uuid()
  const href = `/product?id=${productId}&page_uuid=${pageUuid}`
  const prevRefType = sessionStorage.getItem(SEGMENT_STORAGE_KEY_PRODUCT.type)

  appendObjToSessionObj(SEGMENT_STORAGE_KEY_PRODUCT.stack, {
    [pageUuid]: {
      click_position: idx + 1,
      ref_id: productId,
      ref_type: prevRefType,
      sub_position: '',
    },
  })
  sessionStorage.setItem(SEGMENT_STORAGE_KEY_PRODUCT.id, productId)
  sessionStorage.setItem(SEGMENT_STORAGE_KEY_PRODUCT.position, idx + 1)
  sessionStorage.setItem(SEGMENT_STORAGE_KEY_PRODUCT.type, prevRefType)
  sessionStorage.setItem(SEGMENT_STORAGE_KEY_PRODUCT.subPos, '')

  if (variations.length > 1) {
    sessionStorage.setItem(SEGMENT_STORAGE_KEY_PRODUCT.colorSwatch, true)
  }
  Router.push(href, as)
}

export function getVariations({ variations }, isItemSet) {
  if (isItemSet && variations?.length && variations[0].palettes?.length) {
    return variations[0].palettes
  }

  return variations?.length ? variations : []
}

const AttributesSelector = ({
  product,
  internationalization: { country, language },
  t,
}) => {
  const { id_product, type } = product
  const isSkincare = type === 'cosmetics' || type === 'bath_and_bodyworks'
  const isItemSet = type === 'item_set'
  const title = isSkincare ? 'Formula' : 'Color'
  const categoryRewrite = product?.category?.link_rewrite || 'clothes'
  const variations = getVariations(product, isItemSet)

  const [useVariations, setUseVariations] = useState(variations)
  const [localSelected, setLocalSelected] = useState(null)

  const currentAttribute =
    localSelected || variations.find((attr) => attr.id_product === id_product)

  useEffect(() => {
    if (isItemSet && variations?.length) {
      setLocalSelected(variations[0]) // select first color for item_set
    } else {
      setLocalSelected(null) // to reset state back when id_product is changed
    }

    if (useVariations?.length) {
      const variationIds = useVariations.map(
        (variation) => variation.id_product,
      )

      if (!variationIds.includes(id_product)) {
        setUseVariations(variations)
      }
    }
  }, [id_product, isItemSet, useVariations, variations])

  const renderPDPPalletes = useCallback(() => {
    if (useVariations.length === 0) {
      return null
    }

    return (
      <div className="pdp__attr-palette">
        {useVariations.map((attr, index) => {
          const as = `/${country}/${language}/${categoryRewrite}/${attr.id_product}-${attr.link_rewrite}.html`
          const key = isItemSet ? 'name' : 'id_product'
          const isVariationSelected =
            attr[key] === (localSelected?.[key] || product[key])

          const commonProps = {
            key: attr[key],
            isSelected: isVariationSelected,
            onClick: isItemSet
              ? () => setLocalSelected(attr)
              : (e) => {
                  e.preventDefault()
                  onClickChangeProduct(as, {
                    productId: attr[key],
                    idx: index,
                    variations,
                  })
                },
          }

          return isSkincare ? (
            <BoxIcon {...commonProps} text={attr.name} />
          ) : (
            <ColorIcon
              {...commonProps}
              color={attr.display_color}
              iconImage={attr.pattern_image}
            />
          )
        })}
      </div>
    )
  }, [
    categoryRewrite,
    country,
    isItemSet,
    isSkincare,
    language,
    localSelected,
    product,
    useVariations,
    variations,
  ])

  if (!product || (variations?.length === 1 && !variations[0].name)) {
    return null
  }

  return (
    <div className="pdp__attr-container">
      <div className="pdp__attr-info-wrapper">
        <span className={Typo.subtitle2}>{`${t(title)}:`}</span>
        {currentAttribute?.name && (
          <span className={clsx('pdp__attr-name', Typo.body2)}>
            {currentAttribute.name}
          </span>
        )}
        {currentAttribute?.description && (
          <span className={clsx('pdp__attr-desc', Typo.body2)}>
            {`(${currentAttribute.description})`}
          </span>
        )}
      </div>
      {renderPDPPalletes()}
    </div>
  )
}

AttributesSelector.propTypes = {
  product: PropTypes.shape({
    id_product: PropTypes.number.isRequired,
    category: PropTypes.shape({
      id_category: PropTypes.number,
      link_rewrite: PropTypes.string,
    }).isRequired,
    type: PropTypes.string.isRequired,
    variations: PropTypes.arrayOf(
      PropTypes.shape({
        id_product: PropTypes.number.isRequired,
        name: PropTypes.string,
        description: PropTypes.string,
        display_color: PropTypes.string,
        pattern_image: PropTypes.string,
        palettes: PropTypes.arrayOf(
          PropTypes.shape({
            display_color: PropTypes.string,
            pattern: PropTypes.string,
            pattern_image: PropTypes.string,
            name: PropTypes.string,
            description: PropTypes.string,
          }),
        ),
      }),
    ),
  }).isRequired,
  internationalization: PropTypes.shape({
    country: PropTypes.string,
    language: PropTypes.string,
  }).isRequired,
  t: PropTypes.func.isRequired,
}

export default compose(
  connect((state) => ({
    internationalization: state.internationalization,
  })),
  withI18next(),
)(AttributesSelector)
